/**
 * @fileoverview Phone Number Search Component
 * 
 * This component provides the search interface for phone numbers with
 * configurable filters based on the module context.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { PhoneNumberContext, SearchType } from '../types.js';
import { validateSearchInput, convertLettersToNumbers } from '../utils/helpers.js';

/**
 * Phone Number Search Component
 * 
 * @param {Object} props - Component props
 * @param {PhoneNumberConfig} props.config - Module configuration
 * @param {SearchCriteria} props.searchCriteria - Current search criteria
 * @param {function} props.onCriteriaChange - Callback for criteria changes
 * @param {function} props.onSearch - Callback for search execution
 * @param {boolean} props.canSearch - Whether search can be performed
 * @param {boolean} props.isSearching - Whether search is in progress
 * @param {string} props.error - Current error message
 * @returns {React.ReactElement} Search component
 */
const PhoneNumberSearch = ({
  config,
  searchCriteria,
  onCriteriaChange,
  onSearch,
  canSearch,
  isSearching,
  error
}) => {
  const [searchType, setSearchType] = useState(SearchType.BASIC);
  const [inputErrors, setInputErrors] = useState({});

  // Get enabled filters
  const enabledFilters = useMemo(() => 
    config.searchFilters.filter(filter => filter.enabled),
    [config.searchFilters]
  );

  // Get basic and advanced filters
  const basicFilters = useMemo(() => 
    enabledFilters.filter(filter => 
      ['country', 'area-code', 'city', 'region'].includes(filter.type)
    ),
    [enabledFilters]
  );

  const advancedFilters = useMemo(() => 
    enabledFilters.filter(filter => 
      !['country', 'area-code', 'city', 'region'].includes(filter.type)
    ),
    [enabledFilters]
  );

  // Handle input change with validation
  const handleInputChange = useCallback((field, value) => {
    // Clear previous error for this field
    setInputErrors(prev => ({ ...prev, [field]: null }));

    // Validate input
    const validation = validateSearchInput(value, field);
    if (!validation.valid) {
      setInputErrors(prev => ({ ...prev, [field]: validation.error }));
    }

    // Update criteria
    onCriteriaChange(field, value);
  }, [onCriteriaChange]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (canSearch && !isSearching) {
      onSearch();
    }
  }, [canSearch, isSearching, onSearch]);

  // Render filter input
  const renderFilterInput = useCallback((filter) => {
    const value = searchCriteria[filter.type] || '';
    const hasError = inputErrors[filter.type];
    const isRequired = filter.required;

    const baseInputClasses = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
      hasError 
        ? 'border-red-300 bg-red-50' 
        : 'border-gray-300 bg-white hover:border-gray-400'
    }`;

    switch (filter.type) {
      case 'country':
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'Country'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={`${filter.type}-input`}
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value)}
              className={baseInputClasses}
              required={isRequired}
            >
              <option value="">Select Country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
            </select>
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      case 'region':
        const regions = value === 'US' ? [
          'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID',
          'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC',
          'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
          'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
        ] : ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'State/Province'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={`${filter.type}-input`}
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value)}
              className={baseInputClasses}
              required={isRequired}
            >
              <option value="">Select State/Province</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      case 'area-code':
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'Area Code'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={`${filter.type}-input`}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value.replace(/\D/g, '').slice(0, 3))}
              className={baseInputClasses}
              placeholder={filter.placeholder || 'e.g., 555'}
              maxLength={3}
              required={isRequired}
            />
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      case 'phone-exchange':
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'Phone Exchange'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={`${filter.type}-input`}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value.replace(/\D/g, '').slice(0, 3))}
              className={baseInputClasses}
              placeholder={filter.placeholder || 'e.g., 472'}
              maxLength={3}
              required={isRequired}
            />
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      case 'letter-pattern':
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'Letter Pattern'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              <span className="text-gray-500 text-xs ml-1">(e.g., PARROT)</span>
            </label>
            <input
              id={`${filter.type}-input`}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10))}
              className={baseInputClasses}
              placeholder={filter.placeholder || 'PARROT'}
              maxLength={10}
              required={isRequired}
            />
            {value && (
              <p className="text-xs text-teal-600 mt-1">
                "{value}" → {convertLettersToNumbers(value)}
              </p>
            )}
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      case 'number-pattern':
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || 'Number Pattern'}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              <span className="text-gray-500 text-xs ml-1">(e.g., 1234)</span>
            </label>
            <input
              id={`${filter.type}-input`}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value.replace(/\D/g, '').slice(0, 10))}
              className={baseInputClasses}
              placeholder={filter.placeholder || '1234'}
              maxLength={10}
              required={isRequired}
            />
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={filter.type}>
            <label htmlFor={`${filter.type}-input`} className="block text-sm font-medium text-gray-700 mb-2">
              {filter.label || filter.type}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={`${filter.type}-input`}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(filter.type, e.target.value)}
              className={baseInputClasses}
              placeholder={filter.placeholder}
              required={isRequired}
            />
            {hasError && (
              <p className="text-red-600 text-xs mt-1">{hasError}</p>
            )}
          </div>
        );
    }
  }, [searchCriteria, inputErrors, handleInputChange]);

  return (
    <div className="phone-number-search">
      {/* Search Type Toggle (if advanced search is enabled) */}
      {config.enableAdvancedSearch && advancedFilters.length > 0 && (
        <div className="flex justify-center space-x-4 mb-6">
          <button
            type="button"
            onClick={() => setSearchType(SearchType.BASIC)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              searchType === SearchType.BASIC
                ? 'bg-teal-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Basic Search
          </button>
          <button
            type="button"
            onClick={() => setSearchType(SearchType.ADVANCED)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              searchType === SearchType.ADVANCED
                ? 'bg-teal-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Advanced Search
          </button>
        </div>
      )}

      {/* Context-specific help text */}
      {config.context === PhoneNumberContext.ONBOARDING && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">
                Choose Your Business Phone Number
              </h3>
              <p className="text-sm text-blue-700">
                Select a local phone number to build trust with your customers. You can always purchase additional numbers later.
              </p>
            </div>
          </div>
        </div>
      )}

      {config.context === PhoneNumberContext.DASHBOARD && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-amber-800 mb-1">
                Pro Tip: Multiple Numbers per Parrot
              </h3>
              <p className="text-sm text-amber-700">
                You can assign multiple phone numbers to the same Parrot for different geographic areas while using the same AI configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 dark:bg-gray-800 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">
          {config.context === PhoneNumberContext.ADMIN ? 'Search Available Numbers' : 'Find Your Perfect Number'}
        </h3>

        <form onSubmit={handleSubmit}>
          {searchType === SearchType.BASIC ? (
            // Basic Search Form
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {basicFilters.map(renderFilterInput)}
            </div>
          ) : (
            // Advanced Search Form
            <div className="space-y-6 mb-6">
              {/* Basic filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {basicFilters.map(renderFilterInput)}
              </div>

              {/* Advanced filters */}
              {advancedFilters.length > 0 && (
                <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
                  <h4 className="text-md font-medium text-gray-900 mb-4 dark:text-white">
                    Advanced Search Options
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {advancedFilters.map(renderFilterInput)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={!canSearch || isSearching}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors flex items-center space-x-2 dark:text-white"
            >
              {isSearching && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white dark:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>{isSearching ? 'Searching...' : 'Search Phone Numbers'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberSearch;
